import { Clock, MapPin, ExternalLink } from "lucide-react"
import { Image, Button } from "@city-sage/aurum-avis-labs-components"
import { Stop } from "@city-sage/core-client"
import { Link } from "@tanstack/react-router"
import { MarkdownRenderer } from "./MarkdownRenderer"

interface StopPopupProps {
  stop: Stop
  onClose: () => void
  className?: string
}

export function StopPopup({ stop, onClose, className = "" }: StopPopupProps) {
  // Format address for display
  const formatAddress = () => {
    if (!stop.place.address) return null
    
    const parts = [
      stop.place.address.addressLine1,
      stop.place.address.city,
      stop.place.address.state
    ].filter(Boolean)
    
    return parts.join(', ')
  }

  // Format duration
  const formatDuration = () => {
    if (!stop.durationMinutes) return null
    
    const hours = Math.floor(stop.durationMinutes / 60)
    const minutes = stop.durationMinutes % 60
    
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
    }
    return `${minutes}m`
  }

  const formatTime = (timeString?: string) => {
    if (!timeString) return null
    // timeString is in HH:MM format, convert to readable time
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${minutes} ${ampm}`
  }

  return (
    <div className={`absolute bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-xs z-50 ${className}`}>
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute -top-2 -right-2 bg-white rounded-full border border-gray-200 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
      >
        ×
      </button>

      <div className="flex gap-3">
        {/* Small Image */}
        {stop.imageUrl && (
          <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg">
            <Image 
              src={stop.imageUrl} 
              image={stop.image}
              alt={stop.title} 
              className="object-cover w-full h-full" 
              imageClassName="h-full"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-1">
          {/* Title */}
          <h3 className="font-medium text-gray-900 text-sm line-clamp-1">{stop.title}</h3>

          {/* Address */}
          {formatAddress() && (
            <div className="flex items-center gap-1">
              <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-600 line-clamp-1">{formatAddress()}</span>
            </div>
          )}

          {/* Time and Duration */}
          <div className="flex items-center gap-3 text-xs text-gray-600">
            {stop.startTime && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{formatTime(stop.startTime)}</span>
              </div>
            )}
            {formatDuration() && (
              <div className="text-emerald-600 font-medium">{formatDuration()}</div>
            )}
          </div>

          {/* Description Preview */}
          {stop.description && (
            <div className="text-xs text-gray-600 line-clamp-1 leading-relaxed">
              <MarkdownRenderer content={stop.description} className="text-xs text-gray-600" />
            </div>
          )}
        </div>
      </div>

      {/* Link to full stop page */}
      <div className="mt-3 pt-2 border-t border-gray-100">
        <Link to={`/itineraries/${stop.itineraryId}/stops/${stop.id}`}>
          <Button variant="ghost" size="sm" className="w-full text-xs h-7 px-2 flex items-center gap-1">
            <span>View details</span>
            <ExternalLink className="h-3 w-3" />
          </Button>
        </Link>
      </div>
    </div>
  )
} 